import { publishOAuthResult } from '@/pages/integrations/providers/oauth-connect-lifecycle'
import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@deepguide-ai/ui/button'
import { getIntegrationProvider } from '@/pages/integrations/providers'
import { IntegrationType } from '@deepguide-ai/types'
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export const Route = createFileRoute('/(auth)/auth-callback/$provider')({
  component: IntegrationCallback,
})

type AzureSubscriptionOption = {
  subscriptionId: string
  displayName: string | null
  state: string | null
}

type PendingAzureSelectionResult = {
  requiresSubscriptionSelection: true
  selectionToken: string
  subscriptions: AzureSubscriptionOption[]
}

type GcpProjectOption = {
  projectId: string
  displayName: string | null
  lifecycleState: string | null
}

type PendingGcpSelectionResult = {
  requiresProjectSelection: true
  selectionToken: string
  projects: GcpProjectOption[]
}

type ConfluenceSiteOption = { id: string; name: string; url: string }
type PendingConfluenceSelectionResult = { requiresSiteSelection: true; selectionToken: string; sites: ConfluenceSiteOption[] }

function isPendingConfluenceSelectionResult(value: unknown): value is PendingConfluenceSelectionResult {
  if (!value || typeof value !== 'object') return false
  const result = value as Partial<PendingConfluenceSelectionResult>
  return result.requiresSiteSelection === true && typeof result.selectionToken === 'string' && Array.isArray(result.sites)
    && result.sites.length > 0 && result.sites.every(site => site && typeof site.id === 'string' && typeof site.name === 'string' && typeof site.url === 'string')
}

type TrelloWorkspaceOption = { id: string; name: string; displayName: string }
type PendingTrelloSelectionResult = { requiresWorkspaceSelection: true; selectionToken: string; workspaces: TrelloWorkspaceOption[] }
function isPendingTrelloSelectionResult(value: unknown): value is PendingTrelloSelectionResult {
  if (!value || typeof value !== 'object') return false
  const result = value as Partial<PendingTrelloSelectionResult>
  return result.requiresWorkspaceSelection === true && typeof result.selectionToken === 'string' && Array.isArray(result.workspaces)
    && result.workspaces.length > 0 && result.workspaces.every(item => item && typeof item.id === 'string' && typeof item.name === 'string' && typeof item.displayName === 'string')
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message
  }

  if (typeof error === 'object' && error !== null) {
    const trpcError = error as {
      data?: {
        zodError?: unknown
        message?: string
      }
      message?: string
      error?: string
    }

    if (trpcError.data?.zodError) {
      return 'Invalid data format'
    }
    if (trpcError.message) {
      return trpcError.message
    }
    if (trpcError.data?.message) {
      return trpcError.data.message
    }
    if (trpcError.error) {
      return trpcError.error
    }
  }

  return 'Unknown error occurred'
}

function isPendingAzureSelectionResult(
  value: unknown
): value is PendingAzureSelectionResult {
  return !!value &&
    typeof value === 'object' &&
    'requiresSubscriptionSelection' in value &&
    value.requiresSubscriptionSelection === true &&
    'selectionToken' in value &&
    typeof value.selectionToken === 'string' &&
    'subscriptions' in value &&
    Array.isArray(value.subscriptions)
}

function isPendingGcpSelectionResult(
  value: unknown
): value is PendingGcpSelectionResult {
  return !!value &&
    typeof value === 'object' &&
    'requiresProjectSelection' in value &&
    value.requiresProjectSelection === true &&
    'selectionToken' in value &&
    typeof value.selectionToken === 'string' &&
    'projects' in value &&
    Array.isArray(value.projects)
}

function IntegrationCallback() {
  const params = Route.useParams()
  const [status, setStatus] = useState<'loading' | 'selection' | 'success' | 'error'>(
    'loading'
  )
  const [message, setMessage] = useState('Processing authorization...')
  const [selectionToken, setSelectionToken] = useState<string | null>(null)
  const [selectionKind, setSelectionKind] = useState<'azure' | 'gcp' | 'confluence' | 'trello' | null>(null)
  const [workspaces, setWorkspaces] = useState<TrelloWorkspaceOption[]>([])
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('')
  const [sites, setSites] = useState<ConfluenceSiteOption[]>([])
  const [selectedSiteId, setSelectedSiteId] = useState('')
  const [subscriptions, setSubscriptions] = useState<AzureSubscriptionOption[]>([])
  const [projects, setProjects] = useState<GcpProjectOption[]>([])
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('')
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [isSubmittingSelection, setIsSubmittingSelection] = useState(false)
  const isAuthHandled = useRef<boolean>(false)

  // Get the URL parameters
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('code')
  const state = urlParams.get('state')
  const realmId = urlParams.get('realmId')
  const error = urlParams.get('error')
  const providerName = params.provider ?? 'Unknown'
  const provider = getIntegrationProvider(providerName as IntegrationType)

  const postSuccess = (result: unknown) => {
    publishOAuthResult(state, { type: 'INTEGRATION_AUTH_SUCCESS', provider: providerName, result })
    window.setTimeout(() => window.close(), 2000)
  }

  const postError = (errorMessage: string, cancelled = false) => {
    publishOAuthResult(state, { type: 'INTEGRATION_AUTH_ERROR', provider: providerName, error: errorMessage, cancelled })
  }

  // On component mount, exchange the code for a token
  useEffect(() => {
    if (isAuthHandled.current) {
      return; // already handled, use a ref to avoid re-running the effect
    }
    
    // Check if user cancelled the authorization
    if (error) {
      isAuthHandled.current = true
      
      let errorMessage = 'Authorization failed'
      if (error === 'access_denied') {
        errorMessage = 'Authorization cancelled'
        setMessage('You cancelled the authorization. You can try again anytime.')
      } else {
        errorMessage = `Authorization error: ${error}`
        setMessage(errorMessage)
      }
      
      setStatus('error')
      postError(errorMessage, error === 'access_denied')
      
      // Auto close the window after a short delay
      setTimeout(() => {
        window.close()
      }, 2000)
      
      return
    }
    
    // Handle auth completion
    const handleAuth = async () => {
      if (!code || !state) {
        setStatus('error')
        setMessage('Missing required parameters for authorization.')
        return
      }

      // Get config for current provider

      if (!provider) {
        setStatus('error')
        setMessage(`Unsupported provider: ${providerName}`)
        return
      }

      try {
        // Call the provider-specific completion handler
        let result = undefined
        if (provider.onConnectComplete) {
          result = await provider.onConnectComplete({ code, state, realmId })
        }

        if (isPendingAzureSelectionResult(result)) {
          const options = result.subscriptions.filter(
            (item): item is AzureSubscriptionOption =>
              !!item &&
              typeof item.subscriptionId === 'string' &&
              item.subscriptionId.length > 0
          )
          const defaultSubscriptionId =
            options.find((item) => item.state === 'Enabled')?.subscriptionId ??
            options[0]?.subscriptionId ??
            ''

          setSelectionToken(result.selectionToken)
          setSelectionKind('azure')
          setSubscriptions(options)
          setProjects([])
          setSelectedSubscriptionId(defaultSubscriptionId)
          setSelectedProjectId('')
          setStatus('selection')
          setMessage('Choose the Azure subscription you want to connect.')
          return
        }

        if (isPendingGcpSelectionResult(result)) {
          const options = result.projects.filter(
            (item): item is GcpProjectOption =>
              !!item &&
              typeof item.projectId === 'string' &&
              item.projectId.length > 0
          )
          const defaultProjectId =
            options.find((item) => item.lifecycleState === 'ACTIVE')?.projectId ??
            options[0]?.projectId ??
            ''

          setSelectionToken(result.selectionToken)
          setSelectionKind('gcp')
          setProjects(options)
          setSubscriptions([])
          setSelectedProjectId(defaultProjectId)
          setSelectedSubscriptionId('')
          setStatus('selection')
          setMessage('Choose the Google Cloud project you want to connect.')
          return
        }

        if (providerName === 'Trello' && isPendingTrelloSelectionResult(result)) {
          setSelectionToken(result.selectionToken)
          setSelectionKind('trello')
          setWorkspaces(result.workspaces)
          setSelectedWorkspaceId('')
          setStatus('selection')
          setMessage('Choose the Trello workspace whose evidence you want to collect.')
          return
        }
        if (providerName === 'Trello' && result?.requiresWorkspaceSelection) throw new Error('No valid Trello workspaces were returned. Reconnect and try again.')

        if (isPendingConfluenceSelectionResult(result)) {
          setSelectionToken(result.selectionToken)
          setSelectionKind('confluence')
          setSites(result.sites)
          setSelectedSiteId('')
          setStatus('selection')
          setMessage('Choose the Confluence site whose group access you want to collect.')
          return
        }

        setStatus('success')
        setMessage(`${provider.name} integration connected successfully!`)
        postSuccess(result)

      } catch (error) {
        setStatus('error')
        const errorMessage = extractErrorMessage(error)

        console.error(`${provider.name} integration error:`, error)
        setMessage(`Error connecting ${provider.name}: ${errorMessage}`)
        postError(errorMessage)
      }
    }

    if (code && state && provider && !isAuthHandled.current) {
      isAuthHandled.current = true
      handleAuth()
    }
  }, [code, state, provider, providerName, isAuthHandled])

  // Handle manually closing the window
  const handleClose = () => {
    if (status === 'loading' || status === 'selection') postError('Authorization cancelled', true)
    window.close()
  }

  const handleSelectionConfirm = async () => {
    if (
      !provider?.completePendingConnect ||
      !selectionToken ||
      !selectionKind ||
      (selectionKind === 'azure' && !selectedSubscriptionId) ||
      (selectionKind === 'gcp' && !selectedProjectId) ||
      (selectionKind === 'confluence' && !selectedSiteId) ||
      (selectionKind === 'trello' && !selectedWorkspaceId)
    ) {
      setStatus('error')
      setMessage('Selection is incomplete. Please reconnect and try again.')
      return
    }

    setIsSubmittingSelection(true)

    try {
      const result = await provider.completePendingConnect({
        selectionToken,
        ...(selectionKind === 'azure'
          ? { subscriptionId: selectedSubscriptionId }
          : selectionKind === 'trello' ? { trelloWorkspaceId: selectedWorkspaceId } : selectionKind === 'confluence' ? { siteId: selectedSiteId } : { projectId: selectedProjectId }),
      })
      const selectedSubscription = subscriptions.find(
        (item) => item.subscriptionId === selectedSubscriptionId
      )
      const selectedProject = projects.find(
        (item) => item.projectId === selectedProjectId
      )

      setStatus('success')
      setMessage(
        `${provider.name} integration connected successfully${
          selectedSubscription?.displayName
            ? ` using ${selectedSubscription.displayName}`
            : selectedProject?.displayName
              ? ` using ${selectedProject.displayName}`
            : ''
        }!`
      )
      postSuccess(result)
    } catch (error) {
      const errorMessage = extractErrorMessage(error)
      setStatus('error')
      setMessage(`Error connecting ${provider.name}: ${errorMessage}`)
      postError(errorMessage)
    } finally {
      setIsSubmittingSelection(false)
    }
  }

  // Get display name for the provider
  const providerDisplayName = provider?.name || capitalize(providerName)

  // Helper function to capitalize provider name
  function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  }

  return (
    <div className='bg-background flex min-h-screen w-full items-center justify-center'>
      <div className='bg-card w-full max-w-md rounded-md p-8 text-center shadow-md'>
        {status === 'loading' && (
          <div className='flex flex-col items-center'>
            <Loader2 className='text-primary mb-4 h-16 w-16 animate-spin' />
            <h2 className='mb-4 text-2xl font-bold'>
              Connecting {providerDisplayName}
            </h2>
            <p className='mb-4 text-gray-600 dark:text-text-body'>{message}</p>
          </div>
        )}

        {status === 'success' && (
          <div className='flex flex-col items-center'>
            <CheckCircle className='mb-4 h-16 w-16 text-green-500 dark:text-status-green' />
            <h2 className='mb-4 text-2xl font-bold'>Success!</h2>
            <p className='mb-4 text-gray-600 dark:text-text-body'>{message}</p>
            <p className='mb-4 text-sm text-gray-500 dark:text-text-subtle'>
              This window will close automatically...
            </p>
            <Button onClick={handleClose} className='mt-4'>
              Close Window
            </Button>
          </div>
        )}

        {status === 'selection' && (
          <div className='flex flex-col items-center'>
            <CheckCircle className='mb-4 h-16 w-16 text-blue-500 dark:text-status-blue' />
            <h2 className='mb-4 text-2xl font-bold'>
              {selectionKind === 'trello' ? 'Choose Workspace' : selectionKind === 'confluence' ? 'Choose Site' : selectionKind === 'gcp' ? 'Choose Project' : 'Choose Subscription'}
            </h2>
            <p className='mb-4 text-gray-600 dark:text-text-body'>{message}</p>
            <div className='mb-4 flex w-full flex-col gap-2 text-left'>
              {selectionKind === 'trello' ? workspaces.map(workspace => <Button
                key={workspace.id} variant='outline' aria-pressed={workspace.id === selectedWorkspaceId}
                disabled={isSubmittingSelection} onClick={() => setSelectedWorkspaceId(workspace.id)}
                className='h-auto flex-col items-start whitespace-normal p-3 text-left'
              ><span>{workspace.displayName || workspace.name}</span><span className='break-all text-xs text-muted-foreground'>{workspace.id}</span></Button>) : selectionKind === 'confluence' ? sites.map(site => <Button
                key={site.id} variant='outline' aria-pressed={site.id === selectedSiteId}
                disabled={isSubmittingSelection} onClick={() => setSelectedSiteId(site.id)}
                className={`h-auto flex-col items-start whitespace-normal p-3 text-left ${site.id === selectedSiteId ? 'border-primary bg-primary/5' : ''}`}
              >
                <span>{site.name}</span><span className='break-all text-xs text-muted-foreground'>{site.url}</span>
              </Button>) : selectionKind === 'gcp' ? projects.map((project) => {
                const isSelected = project.projectId === selectedProjectId

                return (
                  <button
                    key={project.projectId}
                    type='button'
                    onClick={() => setSelectedProjectId(project.projectId)}
                    className={`rounded-md border p-3 text-left transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300 dark:border-border-soft dark:hover:border-border-divider-hover'
                    }`}
                  >
                    <div className='flex items-center justify-between gap-3'>
                      <div className='min-w-0'>
                        <div className='font-medium text-gray-900 dark:text-text-secondary'>
                          {project.displayName || project.projectId}
                        </div>
                        <div className='truncate text-sm text-gray-500 dark:text-text-subtle'>
                          {project.projectId}
                        </div>
                      </div>
                      <span className='rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600 dark:bg-status-gray-bg dark:text-status-gray-strong'>
                        {project.lifecycleState || 'Unknown'}
                      </span>
                    </div>
                  </button>
                )
              }) : subscriptions.map((subscription) => {
                const isSelected =
                  subscription.subscriptionId === selectedSubscriptionId

                return (
                  <button
                    key={subscription.subscriptionId}
                    type='button'
                    onClick={() => setSelectedSubscriptionId(subscription.subscriptionId)}
                    className={`rounded-md border p-3 text-left transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300 dark:border-border-soft dark:hover:border-border-divider-hover'
                    }`}
                  >
                    <div className='flex items-center justify-between gap-3'>
                      <div className='min-w-0'>
                        <div className='font-medium text-gray-900 dark:text-text-secondary'>
                          {subscription.displayName || subscription.subscriptionId}
                        </div>
                        <div className='truncate text-sm text-gray-500 dark:text-text-subtle'>
                          {subscription.subscriptionId}
                        </div>
                      </div>
                      <span className='rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600 dark:bg-status-gray-bg dark:text-status-gray-strong'>
                        {subscription.state || 'Unknown'}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
            <div className='flex w-full gap-3'>
              <Button
                variant='outline'
                onClick={handleClose}
                className='flex-1'
                disabled={isSubmittingSelection}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSelectionConfirm}
                className='flex-1'
                disabled={
                  isSubmittingSelection ||
                  (selectionKind === 'azure' && !selectedSubscriptionId) ||
                  (selectionKind === 'gcp' && !selectedProjectId) ||
                  (selectionKind === 'confluence' && !selectedSiteId) ||
                  (selectionKind === 'trello' && !selectedWorkspaceId)
                }
              >
                {isSubmittingSelection ? (
                  <>
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    Connecting...
                  </>
                ) : (
                  selectionKind === 'trello' ? 'Connect Workspace' : selectionKind === 'confluence' ? 'Connect Site' : selectionKind === 'gcp' ? 'Connect Project' : 'Connect Subscription'
                )}
              </Button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className='flex flex-col items-center'>
            <AlertCircle className='mb-4 h-16 w-16 text-destructive' />
            <h2 className='mb-4 text-2xl font-bold'>
              {message.includes('cancelled') ? 'Authorization Cancelled' : 'Connection Failed'}
            </h2>
            <p className='mb-4 text-gray-600 dark:text-text-body'>{message}</p>
            {!message.includes('cancelled') && (
              <div className='mb-4 max-w-full overflow-auto rounded-md border border-status-red/30 bg-status-red-bg p-3 text-sm text-status-red-strong'>
                <p>Please try again or contact support if the issue persists.</p>
              </div>
            )}
            <Button onClick={handleClose} className='mt-4'>
              Close Window
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
